/**
 * Read-time visibility classification for conversation-timeline messages:
 * given a message's stored facts (visible flag, type, event subType), the
 * minimum {@link DebugLevel} a viewer needs to see it.
 *
 * The policy lives HERE, in code, and nowhere else — deliberately never
 * stamped onto rows at write time (Chuck, 2026-08-07). Rows carry only facts
 * (`metadata.subType`); reclassifying an event later means editing this map,
 * and every consumer — REST tier filters, socket room routing, timeline
 * renderers — follows immediately, for all history at once.
 *
 * Consumers (keep in sync when adding one):
 *  - `apps/server` ContactCenterV3 message queries (SQL tier filters)
 *  - `apps/bot` ChatSocket room routing (`convRoomForLevel`) — the emit-side
 *    enforcement that keeps internal rows off unauthorized wires entirely
 *  - `libs/chat-ui` ConversationTimeline (defense-in-depth render gate)
 */
import { BotMessageType } from "../Bot/BotMessage.Interface";
import type { DebugLevel } from "./DebugLevel";

/**
 * Message types that are admin-only whenever invisible.
 *
 * Contact-center agents work for the OPERATOR, not for Swivl (Chuck,
 * 2026-08-18): Swivl's machinery — tool calls and their payloads, picker API
 * fetches, sandbox actions, prompt triggers, system prompts, LLM payloads,
 * analytics — is never their business. The agent tier carries only the
 * conversation itself and the events about people acting on it: the
 * escalation/routing story, tickets, takeovers, nudges, and customer activity
 * (`session_resume`, `page_navigation` — the latter deliberately agent-tier
 * since ENG-2957, 2026-08-14: the right column's "current page" line and the
 * timeline's page dividers are v2-parity agent features). `escalation_event`
 * classifies per subType below.
 */
export const ADMIN_ONLY_MESSAGE_TYPES: readonly BotMessageType[] = [
  BotMessageType.SYSTEM_PROMPT,
  BotMessageType.LLM_CALL,
  // Swivl machinery reclassified off the agent tier (Chuck, 2026-08-18) —
  // agents were never meant to see tool calls or any bot plumbing:
  BotMessageType.TOOL_CALL,
  BotMessageType.PICKER_API_CALL,
  BotMessageType.SANDBOX_ACTION,
  BotMessageType.GREETING_TRIGGER,
  BotMessageType.SEQUENCE_TRIGGER,
  BotMessageType.ANALYTICS_EVENT,
  // Conversation Starter priming records — the same machinery class as the
  // greeting/sequence triggers (ENG-3110).
  BotMessageType.STARTER_TRIGGER,
  BotMessageType.CTA_TRIGGER
  // NOT listed on purpose: `BotMessageType.PROBLEM`. Chuck's ruling (2026-08-23):
  // problem markers are visible to agents as well as Swivl staff and admins, so
  // they classify at the agent tier via the default below. Do not "tidy" it in.
];

/**
 * `escalation_event` subTypes agents may see: the escalation/routing STORY —
 * what happened to the caller's request as it moved through offer → accept /
 * reject / timeout → bridge → completion (all written server-side; catalog in
 * docs/snowy/plans/2026-06-09-voice-escalation-barge-in.md).
 *
 * Everything NOT listed is admin-only — deliberately fail-closed, because the
 * unlisted set is runtime diagnostics (the voice gateway's STT/TTS/noise/audio
 * meter events, presence snapshots) and agents never need technical internals
 * (Chuck, 2026-08-06). A new diagnostic someone forgets to classify stays
 * hidden from agents rather than leaking. To make one agent-visible, add it
 * here — history reclassifies with it.
 */
export const AGENT_VISIBLE_EVENT_SUBTYPES: readonly string[] = [
  "assignment_offered",
  "assignment_accepted",
  "assignment_rejected",
  "assignment_timed_out",
  "assignment_auto_completed",
  "reroute_started",
  "routing_resolved",
  "routing_exhausted",
  "bridge_triggered",
  "escalation_completed",
  "offer_withdrawn",
  "takeover_pulled",
  "conversation_closed",
  // ENG-3005: the conversation's ticket record — created/updated rows with a
  // clickable #number chip. The judge's diagnostic (ticket_update_judge)
  // deliberately stays unlisted → admin-only.
  "ticket_created",
  "ticket_updated",
  // ENG-3018: agent-outbound calls — who placed the call (and from what
  // number), and that direct-dialing an unknown number created the user.
  "outbound_call_initiated",
  "outbound_user_created",
  // ENG-3216 decision 66: a sequence-started conversation says so at the top of the
  // transcript — the sequence, the day, the channel, the recipient, the facility, the
  // outcome. The machinery (prompt, ledger, attempt ids) stays on the admin-only trigger.
  "sequence_started",
  // ENG-3408: the accepted agent's leg never joined — agents need to see why
  // the caller is back with the bot (and why their softphone dropped).
  "agent_leg_failed"
];

/** Minimum level to see an `escalation_event` row with the given subType. */
export function eventMinDebugLevel(subType: string | undefined): DebugLevel {
  return subType !== undefined && AGENT_VISIBLE_EVENT_SUBTYPES.includes(subType) ? "agent" : "admin";
}

/**
 * The message facts classification reads. Structurally satisfied by both
 * `BotMessageEntity` and the canonical `ConversationMessage` wire shape.
 */
export interface MessageDebugFacts {
  /** Only an explicit `false` marks a message internal; absent means user-visible. */
  visible?: boolean;
  type?: string;
  metadata?: unknown;
}

/** Minimum {@link DebugLevel} required to see the given message. */
export function messageMinDebugLevel(msg: MessageDebugFacts): DebugLevel {
  if (msg.visible !== false) return "none";
  const type = msg.type ?? "";
  if ((ADMIN_ONLY_MESSAGE_TYPES as readonly string[]).includes(type)) return "admin";
  if (type === BotMessageType.ESCALATION_EVENT) {
    const subType = (msg.metadata as { subType?: unknown } | null | undefined)?.subType;
    return eventMinDebugLevel(typeof subType === "string" ? subType : undefined);
  }
  return "agent";
}
